import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileText, CheckCircle, Loader2, GitCompare, AlertCircle, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OpeningBalanceScrutiny = () => {
  const [file1, setFile1] = useState(null);
  const [file2, setFile2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [symbolFilter, setSymbolFilter] = useState('all');

  const handleFileChange = (fileNum, event) => {
    const file = event.target.files[0];
    if (file && !file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Please upload only Excel files (.xlsx, .xls)');
      return;
    }
    if (fileNum === 1) setFile1(file);
    else setFile2(file);
  };

  const handleAnalyze = async () => {
    if (!file1 || !file2) {
      toast.error('Please upload both files');
      return;
    }

    setLoading(true);
    const formData = new FormData();
    formData.append('file1', file1);
    formData.append('file2', file2);

    try {
      const response = await axios.post(`${API}/trial-balance/opening-scrutiny`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResults(response.data);
      toast.success('Analysis completed and saved successfully!');
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to analyze files');
    } finally {
      setLoading(false);
    }
  };

  const getSymbolBadge = (symbol, remark) => {
    if (symbol === '✅') return <Badge variant="default" className="bg-green-500">✓ Matched</Badge>;
    if (symbol === '❌') return <Badge variant="destructive">✗ Mismatch</Badge>;
    if (symbol === '⚠️') return <Badge variant="secondary">⚠ Warning</Badge>;
    if (remark.includes('Newly created')) return <Badge variant="outline">New</Badge>;
    return <Badge variant="outline">{symbol || '-'}</Badge>;
  };

  const getSymbolCategory = (symbol, remark) => {
    if (symbol === '✅') return 'matched';
    if (symbol === '❌') return 'mismatch';
    if (symbol === '⚠️') return 'warning';
    if (remark?.includes('Newly created')) return 'new';
    return 'other';
  };

  // Filter function
  const filterRecords = useCallback((records) => {
    if (!records) return [];
    
    return records.filter(row => {
      // Search filter
      const matchesSearch = !searchTerm || 
        row.particulars?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        row.remark?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // Symbol filter
      const symbolCategory = getSymbolCategory(row.symbol, row.remark);
      const matchesSymbol = symbolFilter === 'all' || symbolCategory === symbolFilter;
      
      return matchesSearch && matchesSymbol;
    });
  }, [searchTerm, symbolFilter]);

  const filteredPrev = useMemo(() => 
    filterRecords(results?.prev), 
    [results, filterRecords]
  );

  const filteredCurr = useMemo(() => 
    filterRecords(results?.curr), 
    [results, filterRecords]
  );

  return (
    <div className="space-y-6" data-testid="opening-scrutiny-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <GitCompare className="h-8 w-8 mr-3 text-orange-600" />
            Opening Balance Scrutiny
          </h1>
          <p className="text-gray-600 mt-2">
            Compare previous year closing balances with current year opening balances
          </p>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 mr-3" />
            <div>
              <h3 className="font-semibold text-blue-900">About Opening Balance Scrutiny</h3>
              <p className="text-sm text-blue-800 mt-1">
                This tool compares the closing balances from previous year with opening balances of current year. It helps identify discrepancies, newly created ledgers, and removed accounts.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* File Upload Section */}
      <Card>
        <CardHeader>
          <CardTitle>Upload Trial Balance Files</CardTitle>
          <CardDescription>
            Upload both previous year and current year trial balance files
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="file1">Previous Year Trial Balance (.xlsx)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file1"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileChange(1, e)}
                  className="cursor-pointer"
                  data-testid="previous-year-file-input"
                />
                {file1 && <CheckCircle className="h-5 w-5 text-green-600" />}
              </div>
              {file1 && <p className="text-sm text-gray-600">{file1.name}</p>}
            </div>

            <div className="space-y-2">
              <Label htmlFor="file2">Current Year Trial Balance (.xlsx)</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file2"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => handleFileChange(2, e)}
                  className="cursor-pointer"
                  data-testid="current-year-file-input"
                />
                {file2 && <CheckCircle className="h-5 w-5 text-green-600" />}
              </div>
              {file2 && <p className="text-sm text-gray-600">{file2.name}</p>}
            </div>
          </div>

          <Button
            onClick={handleAnalyze}
            disabled={!file1 || !file2 || loading}
            className="mt-6 w-full md:w-auto"
            data-testid="analyze-button"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <FileText className="h-4 w-4 mr-2" />
                Compare Balances
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section with Filters */}
      {results && (
        <>
          {/* Filters Card */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filters
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-64">
                  <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Search by Particulars or Remark..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                      data-testid="search-input"
                    />
                  </div>
                </div>
                
                <Select value={symbolFilter} onValueChange={setSymbolFilter}>
                  <SelectTrigger className="w-48" data-testid="symbol-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="matched">Matched</SelectItem>
                    <SelectItem value="mismatch">Mismatch</SelectItem>
                    <SelectItem value="warning">Warning</SelectItem>
                    <SelectItem value="new">Newly Created</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Results Card */}
          <Card>
            <CardHeader>
              <CardTitle>Scrutiny Results</CardTitle>
              <CardDescription>
                Review the comparison between previous and current year balances
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="previous" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="previous">
                    Previous Year ({filteredPrev.length} of {results.prev?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="current">
                    Current Year ({filteredCurr.length} of {results.curr?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="previous" className="mt-4">
                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Particulars</TableHead>
                          <TableHead>Opening Balance</TableHead>
                          <TableHead>Opening Type</TableHead>
                          <TableHead>Closing Balance</TableHead>
                          <TableHead>Closing Type</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredPrev.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                              No records match the current filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredPrev.map((row, idx) => (
                            <TableRow key={idx} className={row.symbol === '❌' ? 'bg-red-50' : row.symbol === '⚠️' ? 'bg-yellow-50' : ''}>
                              <TableCell>{getSymbolBadge(row.symbol, row.remark)}</TableCell>
                              <TableCell className="font-medium">{row.particulars}</TableCell>
                              <TableCell>{row.opening_balance}</TableCell>
                              <TableCell>
                                {row.opening_balance_type === 0 ? 'Credit' : row.opening_balance_type === 1 ? 'Debit' : '-'}
                              </TableCell>
                              <TableCell>{row.closing_balance}</TableCell>
                              <TableCell>
                                {row.closing_balance_type === 0 ? 'Credit' : row.closing_balance_type === 1 ? 'Debit' : '-'}
                              </TableCell>
                              <TableCell className="text-sm">{row.remark}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>

                <TabsContent value="current" className="mt-4">
                  <div className="rounded-md border overflow-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Status</TableHead>
                          <TableHead>Particulars</TableHead>
                          <TableHead>Opening Balance</TableHead>
                          <TableHead>Opening Type</TableHead>
                          <TableHead>Closing Balance</TableHead>
                          <TableHead>Closing Type</TableHead>
                          <TableHead>Remark</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredCurr.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                              No records match the current filters
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredCurr.map((row, idx) => (
                            <TableRow key={idx} className={row.symbol === '❌' ? 'bg-red-50' : row.symbol === '⚠️' ? 'bg-yellow-50' : row.remark?.includes('Newly created') ? 'bg-blue-50' : ''}>
                              <TableCell>{getSymbolBadge(row.symbol, row.remark)}</TableCell>
                              <TableCell className="font-medium">{row.particulars}</TableCell>
                              <TableCell>{row.opening_balance}</TableCell>
                              <TableCell>
                                {row.opening_balance_type === 0 ? 'Credit' : row.opening_balance_type === 1 ? 'Debit' : '-'}
                              </TableCell>
                              <TableCell>{row.closing_balance}</TableCell>
                              <TableCell>
                                {row.closing_balance_type === 0 ? 'Credit' : row.closing_balance_type === 1 ? 'Debit' : '-'}
                              </TableCell>
                              <TableCell className="text-sm">{row.remark}</TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default OpeningBalanceScrutiny;
